import { useState, useEffect } from 'react'
import { usePublicContent } from './usePublicContent'
import { SiteSettings } from '../types/content'
import { updateFeatures } from '../config/features'

export const useSiteSettings = () => {
  const { content, loading } = usePublicContent('site-settings')
  const [settings, setSettings] = useState<SiteSettings | null>(null)

  useEffect(() => {
    if (content?.data) {
      const siteData = content.data as SiteSettings
      setSettings(siteData)
      applyFonts(siteData.fonts)
      applyTheme(siteData.theme)
      
      // Apply feature flags if they exist
      if (siteData.features) {
        updateFeatures({
          CONTACT_FORM: siteData.features.contactForm,
          PRICING_GUIDE: siteData.features.pricingGuide
        })
      }
    }
  }, [content])

  const applyFonts = (fonts: SiteSettings['fonts']) => {
    // Load Google Fonts
    const fontFamilies = [fonts.primaryFont, fonts.secondaryFont, fonts.headingFont]
      .filter((font, index, arr) => arr.indexOf(font) === index) // Remove duplicates
      .map(font => font.replace(/\s+/g, '+'))
      .join('|')

    if (fontFamilies) {
      // Remove existing Google Fonts link
      const existingLink = document.querySelector('link[href*="fonts.googleapis.com"]')
      if (existingLink) {
        existingLink.remove()
      }

      // Add new Google Fonts link
      const link = document.createElement('link')
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@300;400;500;600;700&display=swap`
      link.rel = 'stylesheet'
      document.head.appendChild(link)

      // Apply CSS custom properties
      const root = document.documentElement
      root.style.setProperty('--font-primary', `"${fonts.primaryFont}", sans-serif`)
      root.style.setProperty('--font-secondary', `"${fonts.secondaryFont}", sans-serif`)
      root.style.setProperty('--font-heading', `"${fonts.headingFont}", sans-serif`)
      
      // Apply font weights
      if (fonts.fontWeights) {
        root.style.setProperty('--font-weight-body', fonts.fontWeights.body.toString())
        root.style.setProperty('--font-weight-headings', fonts.fontWeights.headings.toString())
        root.style.setProperty('--font-weight-buttons', fonts.fontWeights.buttons.toString())
      }
    }
  }

  const applyTheme = (theme: SiteSettings['theme']) => {
    const root = document.documentElement
    root.style.setProperty('--color-primary', theme.primaryColor)
    root.style.setProperty('--color-secondary', theme.secondaryColor)
  }

  return {
    settings,
    loading,
    applyFonts,
    applyTheme
  }
}
